// src/client/pages/users.tsx
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@client/components/ui/button";
import { Input } from "@client/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@client/components/ui/card";
import { Label } from "@client/components/ui/label";
import { Switch } from "@client/components/ui/switch";
import { Badge } from "@client/components/ui/badge";
import { useToast } from "@client/hooks/use-toast";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@client/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@client/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@client/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@client/components/ui/tooltip";
import { Separator } from "@client/components/ui/separator";
import { User as UserIcon, Edit, Trash2, Plus, Save, X, Key, Eye, EyeOff, AlertTriangle, Shield } from "lucide-react";
import { ApiClient } from "@client/lib/api-client";
import { queryKeys, invalidationHelpers } from "@client/lib/query-keys";
import type {
  UsersPageProps,
  User,
  GetUsersResponse,
  CreateUserRequest,
  UpdateUserRequest,
  UserFormData,
} from "@client/types";

// ============================================================================
// Local Types
// ============================================================================

interface PasswordChangeData {
  newPassword: string;
  confirmPassword: string;
}

type DialogMode = 'create' | 'edit' | 'password' | null;

// ============================================================================
// Validation Functions
// ============================================================================

function validateUserForm(formData: UserFormData, isCreating: boolean): string | null {
  if (!formData.username.trim()) {
    return "Benutzername ist erforderlich.";
  }
  
  if (!formData.displayName.trim()) {
    return "Anzeigename ist erforderlich.";
  }
  
  if (!formData.email.trim()) {
    return "E-Mail ist erforderlich.";
  }
  
  // Basic email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(formData.email)) {
    return "Ungültige E-Mail-Adresse.";
  }
  
  if (isCreating && !formData.password) {
    return "Passwort ist für neue Benutzer erforderlich.";
  }
  
  if (isCreating && formData.password && formData.password.length < 6) {
    return "Passwort muss mindestens 6 Zeichen lang sein.";
  }
  
  return null;
}

function validatePasswordChange(data: PasswordChangeData): string | null {
  if (!data.newPassword || !data.confirmPassword) {
    return "Beide Passwort-Felder sind erforderlich.";
  }
  
  if (data.newPassword !== data.confirmPassword) {
    return "Die Passwörter stimmen nicht überein.";
  }
  
  if (data.newPassword.length < 6) {
    return "Das Passwort muss mindestens 6 Zeichen lang sein.";
  }
  
  return null;
}

// ============================================================================
// Loading Skeleton Component
// ============================================================================

function UsersSkeleton() {
  return (
    <div className="p-6">
      <Card>
        <CardHeader>
          <div className="h-6 w-32 bg-gray-200 rounded animate-pulse" />
          <div className="h-4 w-64 bg-gray-100 rounded animate-pulse mt-2" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// Empty State Component
// ============================================================================

function EmptyState({ onCreateClick }: { onCreateClick: () => void }) {
  return (
    <div className="text-center py-12">
      <UserIcon className="h-16 w-16 mx-auto mb-4 text-gray-400" />
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        Keine Benutzer gefunden
      </h3>
      <p className="text-sm text-muted-foreground mb-6">
        Erstellen Sie einen neuen Benutzer, um zu beginnen.
      </p>
      <Button onClick={onCreateClick}>
        <Plus className="h-4 w-4 mr-2" />
        Ersten Benutzer erstellen
      </Button>
    </div>
  );
}

// ============================================================================
// Main Users Component
// ============================================================================

export default function Users(_props: UsersPageProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Dialog state
  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [passwordVisible, setPasswordVisible] = useState(false);

  // Form state
  const [formData, setFormData] = useState<UserFormData>({
    username: "",
    displayName: "",
    email: "",
    isActive: true,
    password: ""
  });

  // Password change state
  const [passwordData, setPasswordData] = useState<PasswordChangeData>({
    newPassword: "",
    confirmPassword: ""
  });

  // ============================================================================
  // Queries
  // ============================================================================

  const { data: users = [], isLoading, error } = useQuery<GetUsersResponse, Error>({
    queryKey: queryKeys.users.lists(),
    queryFn: async () => {
      const data = await ApiClient.get<GetUsersResponse>("/users");
      return Array.isArray(data) ? data : [];
    }
  });

  // ============================================================================
  // Mutations
  // ============================================================================

  const createUserMutation = useMutation({
    mutationFn: (data: CreateUserRequest) => 
      ApiClient.post<User>("/users", data),
    onSuccess: () => {
      invalidationHelpers.invalidateUsers(queryClient);
      closeDialog();
      toast({
        title: "Benutzer erstellt",
        description: "Der neue Benutzer wurde erfolgreich hinzugefügt.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Fehler",
        description: error.message || "Benutzer konnte nicht erstellt werden.",
        variant: "destructive",
      });
    }
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateUserRequest }) => 
      ApiClient.put<User>(`/users/${id}`, data),
    onSuccess: () => {
      invalidationHelpers.invalidateUsers(queryClient);
      closeDialog();
      toast({
        title: "Benutzer aktualisiert",
        description: "Der Benutzer wurde erfolgreich geändert.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Fehler",
        description: error.message || "Benutzer konnte nicht aktualisiert werden.",
        variant: "destructive",
      });
    }
  });

  const changePasswordMutation = useMutation({
    mutationFn: ({ userId, newPassword }: { userId: number; newPassword: string }) => {
      console.log('[Users] Calling PUT /users/' + userId + '/password');
      return ApiClient.put<{ success: boolean; message: string }>(`/users/${userId}/password`, { newPassword });
    },
    onSuccess: (response) => {
      closeDialog();
      toast({
        title: "Passwort geändert",
        description: response.message || "Das Passwort wurde erfolgreich aktualisiert.",
      });
    },
    onError: (error: any) => {
      console.error('[Users] Password change failed:', error);
      toast({
        title: "Fehler",
        description: error.message || "Passwort konnte nicht geändert werden.",
        variant: "destructive",
      });
    }
  });

  const deleteUserMutation = useMutation({
    mutationFn: (id: number) => ApiClient.delete(`/users/${id}`),
    onSuccess: () => {
      invalidationHelpers.invalidateUsers(queryClient);
      toast({
        title: "Benutzer gelöscht",
        description: "Der Benutzer wurde erfolgreich entfernt.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Fehler",
        description: error.message || "Benutzer konnte nicht gelöscht werden.",
        variant: "destructive",
      });
    }
  });

  // ============================================================================
  // Helper Functions
  // ============================================================================

  const resetForm = () => {
    setFormData({
      username: "",
      displayName: "",
      email: "",
      isActive: true,
      password: ""
    });
  };

  const resetPasswordForm = () => {
    setPasswordData({
      newPassword: "",
      confirmPassword: ""
    });
    setPasswordVisible(false);
  };

  const closeDialog = () => {
    setDialogMode(null);
    setEditingUser(null);
    resetForm();
    resetPasswordForm();
  };

  // ============================================================================
  // Event Handlers
  // ============================================================================

  const handleCreate = () => {
    resetForm();
    setDialogMode('create');
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      displayName: user.displayName,
      email: user.email,
      isActive: user.isActive
    });
    setDialogMode('edit');
  };

  const handleChangePassword = (user: User) => {
    setEditingUser(user);
    resetPasswordForm();
    setDialogMode('password');
  };

  const handleSaveUser = () => {
    const isCreating = dialogMode === 'create';
    const validationError = validateUserForm(formData, isCreating);
    
    if (validationError) {
      toast({
        title: "Validierungsfehler",
        description: validationError,
        variant: "destructive",
      });
      return;
    }

    if (isCreating) {
      createUserMutation.mutate(formData as CreateUserRequest);
    } else if (editingUser) {
      const { password, ...updateData } = formData;
      updateUserMutation.mutate({
        id: editingUser.id,
        data: updateData
      });
    }
  };

  const handleSavePassword = () => {
    const validationError = validatePasswordChange(passwordData);
    
    if (validationError) {
      toast({
        title: "Validierungsfehler",
        description: validationError,
        variant: "destructive",
      });
      return;
    }

    if (editingUser) {
      console.log('[Users] Password change for user:', editingUser.username, '(ID:', editingUser.id + ')');
      changePasswordMutation.mutate({
        userId: editingUser.id,
        newPassword: passwordData.newPassword
      });
    }
  };

  const handleDelete = (id: number) => {
    deleteUserMutation.mutate(id);
  };

  // ============================================================================
  // Render States
  // ============================================================================

  if (isLoading) {
    return <UsersSkeleton />;
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-800">Fehler beim Laden</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-red-700">{error.message}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isPending = createUserMutation.isPending || 
                    updateUserMutation.isPending || 
                    changePasswordMutation.isPending;

  // ============================================================================
  // Main Render
  // ============================================================================

  return (
    <TooltipProvider>
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 shadow-sm border-b border-slate-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-end">
          <Button onClick={handleCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Neuer Benutzer
          </Button>
        </div>
      </header>
      
      <div className="p-6 space-y-6">
        {/* Users Table */}
        <Card>
          <CardHeader>
            <CardTitle>Benutzer</CardTitle>
            <CardDescription>
              Übersicht aller Systembenutzer mit ihren Rollen und Status
            </CardDescription>
          </CardHeader>
          <CardContent>
            {users.length === 0 ? (
              <EmptyState onCreateClick={handleCreate} />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Benutzername</TableHead>
                    <TableHead>Anzeigename</TableHead>
                    <TableHead>E-Mail</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Erstellt am</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.username}</TableCell>
                      <TableCell>{user.displayName}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Badge variant={user.isActive ? "default" : "secondary"}>
                          {user.isActive ? "Aktiv" : "Inaktiv"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(user.createdAt).toLocaleDateString('de-DE')}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* Edit Button */}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEdit(user)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Benutzer bearbeiten</p>
                            </TooltipContent>
                          </Tooltip>

                          {/* Change Password Button */}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleChangePassword(user)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <Key className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Passwort ändern</p>
                            </TooltipContent>
                          </Tooltip>

                          {/* Delete Button */}
                          <AlertDialog>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </AlertDialogTrigger>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Benutzer löschen</p>
                              </TooltipContent>
                            </Tooltip>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle className="flex items-center gap-2">
                                  <AlertTriangle className="h-5 w-5 text-red-600" />
                                  Benutzer löschen?
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  Sind Sie sicher, dass Sie den Benutzer <strong>"{user.username}"</strong> löschen möchten? 
                                  Diese Aktion kann nicht rückgängig gemacht werden und der Benutzer verliert 
                                  dauerhaft den Zugang zum System.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(user.id)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Löschen
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit User Dialog */}
        <Dialog 
          open={dialogMode === 'create' || dialogMode === 'edit'} 
          onOpenChange={(open) => !open && closeDialog()}
        >
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>
                {dialogMode === 'create' ? 'Neuer Benutzer' : 'Benutzer bearbeiten'}
              </DialogTitle>
              <DialogDescription>
                {dialogMode === 'create' 
                  ? 'Erstellen Sie ein neues Benutzerkonto für das System.'
                  : `Ändern Sie die Eigenschaften des Benutzers "${editingUser?.username}".`
                }
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="username">Benutzername *</Label>
                  <Input
                    id="username"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="benutzername"
                    disabled={isPending}
                  />
                </div>
                <div>
                  <Label htmlFor="displayName">Anzeigename *</Label>
                  <Input
                    id="displayName"
                    value={formData.displayName}
                    onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    placeholder="Max Mustermann"
                    disabled={isPending}
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="email">E-Mail *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="user@example.com"
                  disabled={isPending}
                />
              </div>

              {dialogMode === 'create' && (
                <div>
                  <Label htmlFor="password">Passwort *</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={passwordVisible ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="••••••••"
                      className="pr-10"
                      disabled={isPending}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setPasswordVisible(!passwordVisible)}
                      disabled={isPending}
                    >
                      {passwordVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                  disabled={isPending}
                />
                <Label htmlFor="isActive">Benutzer ist aktiv</Label>
              </div>

              {dialogMode === 'edit' && (
                <>
                  <Separator />
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-2">
                      Passwort separat ändern
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setDialogMode('password');
                      }}
                      disabled={isPending}
                    >
                      <Key className="h-4 w-4 mr-2" />
                      Passwort ändern
                    </Button>
                  </div>
                </>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={closeDialog} disabled={isPending}>
                <X className="h-4 w-4 mr-2" />
                Abbrechen
              </Button>
              <Button onClick={handleSaveUser} disabled={isPending}>
                <Save className="h-4 w-4 mr-2" />
                {dialogMode === 'create' ? 'Erstellen' : 'Aktualisieren'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Change Password Dialog */}
        <Dialog 
          open={dialogMode === 'password'} 
          onOpenChange={(open) => !open && closeDialog()}
        >
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-600" />
                Passwort ändern
              </DialogTitle>
              <DialogDescription>
                Neues Passwort für Benutzer "{editingUser?.username}" setzen.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              handleSavePassword();
            }}>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="new-password">Neues Passwort *</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      name="new-password"
                      type={passwordVisible ? "text" : "password"}
                      autoComplete="new-password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                      placeholder="••••••••"
                      className="pr-10"
                      disabled={isPending}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setPasswordVisible(!passwordVisible)}
                      disabled={isPending}
                    >
                      {passwordVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirm-password">Passwort bestätigen *</Label>
                  <Input
                    id="confirm-password"
                    name="confirm-password"
                    type={passwordVisible ? "text" : "password"}
                    autoComplete="new-password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    placeholder="••••••••"
                    disabled={isPending}
                  />
                </div>

                <div className="bg-blue-50 p-3 rounded-md">
                  <p className="text-sm text-blue-800">
                    <strong>Sicherheitshinweis:</strong> Das neue Passwort sollte mindestens 6 Zeichen lang sein 
                    und eine Kombination aus Buchstaben, Zahlen und Sonderzeichen enthalten.
                  </p>
                </div>
              </div>
              
              <DialogFooter className="mt-4">
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={closeDialog}
                  disabled={isPending}
                >
                  <X className="h-4 w-4 mr-2" />
                  Abbrechen
                </Button>
                <Button 
                  type="submit"
                  disabled={isPending}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Key className="h-4 w-4 mr-2" />
                  Passwort ändern
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}